// node_modules
import { FC, useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBackward, faBold, faForward, faHighlighter, faItalic, faListOl, faListUl, faSubscript, faSuperscript, faUnlink } from "@fortawesome/free-solid-svg-icons";
import { MarkType, NodeType, Schema } from 'prosemirror-model';
import { EditorState, Plugin, Transaction } from 'prosemirror-state';
import { EditorProps, EditorView } from 'prosemirror-view';
import { wrapInList, liftListItem, sinkListItem } from 'prosemirror-schema-list';
import * as commands from "prosemirror-commands";
// Schemas
import { findestMarkdownParser, findestMarkdownSerializer, findestSchema } from "./FindestDefaultSchema";
// styles
import styles from "./proseMirrorEditor.module.scss";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { ToastHelperSingleton } from "Helpers";

type TProseMirrorEditorProps = {
    source: string,
    schema?: Schema,
    plugins: Plugin[],
    editorProps?: EditorProps,
    documentChanged?: (text: string) => void,
    extraClassNameWisiwygBottomContainer?: string,
    extraClassNameWisiwygContent?: string,
};

export const ProseMirrorEditor: FC<TProseMirrorEditorProps> = ({ source, schema, plugins, editorProps, documentChanged, extraClassNameWisiwygBottomContainer, extraClassNameWisiwygContent }: TProseMirrorEditorProps) => {
    const currentSchema = schema ? schema : findestSchema;

    const [state, setEditorState] = useState(() => {
        const currentDoc = findestMarkdownParser.parse(source);
        return EditorState.create({ doc: currentDoc ?? undefined, schema: currentSchema, plugins });
    });

    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [isSubscript, setIsSubscript] = useState(false);
    const [isSuperscript, setIsSuperscript] = useState(false);
    const [isLink, setIsLink] = useState(false);
    const [isInList, setIsInList] = useState(false);

    const updateMarkupStates = () => {
        // Determine the start and end position of the current selection
        let selectionParent = view.state.selection.$from.parent;
        let fromPosition = view.state.selection.$from.parentOffset - 1;
        let toPosition = view.state.selection.$to.parentOffset;

        // Determine if the current range has one of possible marks
        let isCurrentBold = selectionParent.rangeHasMark(fromPosition, toPosition, findestSchema.marks.strong);
        let isCurrentItalic = selectionParent.rangeHasMark(fromPosition, toPosition, findestSchema.marks.em);
        let isCurrentSubscript = selectionParent.rangeHasMark(fromPosition, toPosition, findestSchema.marks.subscript);
        let isCurrentSuperscript = selectionParent.rangeHasMark(fromPosition, toPosition, findestSchema.marks.superscript);
        let isCurrentLink = selectionParent.rangeHasMark(fromPosition, toPosition, findestSchema.marks.link);
        let isCurrentHighlight = selectionParent.rangeHasMark(fromPosition, toPosition, findestSchema.marks.marked);

        // Check which marks don't exist in the current range, but are available in the curren state
        if(view.state.storedMarks) {
            isCurrentBold = false;
            isCurrentItalic = false;
            isCurrentSubscript = false;
            isCurrentSuperscript = false;
            isCurrentLink = false;
            isCurrentHighlight = false;
            for (let i = 0; i < view.state.storedMarks.length; i++) {
                const mark = view.state.storedMarks[i];
                if (mark.type.name === "strong") {
                    isCurrentBold = true;
                } else if (mark.type.name === "em") {
                    isCurrentItalic = true;
                } else if (mark.type.name === "subscript") {
                    isCurrentSubscript = true;
                } else if (mark.type.name === "superscript") {
                    isCurrentSuperscript = true;
                } else if (mark.type.name === "link") {
                    isCurrentLink = true;
                } else if (mark.type.name === "highlight") {
                    isCurrentHighlight = true;
                }
            }
        }

        // Set the status of the marks
        setIsBold(isCurrentBold);
        setIsItalic(isCurrentItalic);
        setIsHighlighted(isCurrentHighlight);
        setIsSubscript(isCurrentSubscript);
        setIsSuperscript(isCurrentSuperscript);
        setIsLink(isCurrentLink);

        // Determine if the cursor is currently in a list
        let currentDepth = view.state.selection.$anchor.depth;
        let listParent = view.state.selection.$anchor.node(currentDepth - 1);
        setIsInList(listParent.type.name === "list_item");
    }

    const updateStateUsingTransaction = (view: any, transaction: Transaction) => {
        const state = view.state.apply(transaction);
        view.updateState(state);
        setEditorState(state);
        updateMarkupStates();
        
        // do not call document changed handler until we add/remove characters 
        // (including white-space, line-break, etc...)
        if (documentChanged && transaction.steps && transaction.steps.length > 0) {

            const updatedMarkdown = findestMarkdownSerializer.serialize(view.state.doc);
            documentChanged(updatedMarkdown);
        }
    }

    const createEditorView = () => {
        return new EditorView(null, {
            ...editorProps,
            state,
            dispatchTransaction: function (transaction: Transaction) {
                updateStateUsingTransaction(this, transaction);
            },
        });
    };

    const [view] = useState(() => {
        return createEditorView();
    });

    const editorRef = useCallback((element: HTMLDivElement | null) => {
        if (element) {
            element.appendChild(view.dom);
        }
    }, [view]);

    const applyFunctionOnViewState = (givenFunction: (state: EditorState, dispatch?: (tr: Transaction) => void) => boolean) => {
        givenFunction(view.state, function (tr: Transaction) {
            updateStateUsingTransaction(view, tr);
        });
    }

    const applyMark = (mark: MarkType) => {
        const markToApply = commands.toggleMark(mark);
        applyFunctionOnViewState(markToApply);
    };

    const menuBoldClick = () => {
        applyMark(findestSchema.marks.strong);   
        if (isBold) {
            setIsBold(false);
        } else {
            setIsBold(true); 
        }
    };
    const menuItalicClick = () => {
        applyMark(findestSchema.marks.em);     
        if (isItalic) {
            setIsItalic(false);
        } else {
            setIsItalic(true); 
        }   
    };
    const menuHighlightClick = () => {
        applyMark(findestSchema.marks.marked);
        if (isHighlighted) {
            setIsHighlighted(false);
        } else {
            setIsHighlighted(true);
        }
    };

    const applyListNode = (nodeType: NodeType) => {
        const listToApply = wrapInList(nodeType);
        applyFunctionOnViewState(listToApply);
        if (isInList) {
            setIsInList(false);
        } else {
            setIsInList(true);
        }
    }

    const menuOrderdedListClick = () => {
        applyListNode(findestSchema.nodes.ordered_list);
    }
    
    const menuUnorderedListClick = () => {
        applyListNode(findestSchema.nodes.bullet_list);
    }

    const reduceListDepth = () => {
        const applyListDepthReduction = liftListItem(findestSchema.nodes.list_item);
        applyFunctionOnViewState(applyListDepthReduction);
    }

    const increaseListDepth = () => {
        const applyListDepthIncrease = sinkListItem(findestSchema.nodes.list_item);
        applyFunctionOnViewState(applyListDepthIncrease);
    }

    const menuUnlink = () => {
        applyMark(findestSchema.marks.link);
    }

    const applySubOrSuperScript = (isSubscript: boolean) => {
        // Determine which script type should be applied and check the other for not being applied yet
        let currentScriptType = isSubscript ? findestSchema.marks.subscript : findestSchema.marks.superscript;
        let oppositeScriptType = isSubscript ? findestSchema.marks.superscript : findestSchema.marks.subscript;

        // Determine the start and end position of the current selection
        let selectionParent = view.state.selection.$from.parent;
        let fromPosition = view.state.selection.$from.parentOffset - 1;
        let toPosition = view.state.selection.$to.parentOffset;

        // Check if the current selection already has the other script type applied
        let isAlreadyOtherScript = selectionParent.rangeHasMark(fromPosition, fromPosition + 1, oppositeScriptType) &&
            selectionParent.rangeHasMark(toPosition - 1, toPosition, oppositeScriptType);

        if(!isAlreadyOtherScript) {
            // Apply the choosen script type
            applyMark(currentScriptType);
        } else {
            // If the other script type is already applied then show an error message
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Cannot use sub and superscript simultaneously.");
        }
    }

    const menuSubscriptClick = () => {
        applySubOrSuperScript(true);
    }
    const menuSuperscriptClick = () => {
        applySubOrSuperScript(false);
    }

    const getMenuButtonClassName = (isActive: boolean) => {
        let classNameList = [styles.menuButton];
        if(isActive) classNameList.push(styles.active);
        let className = classNameList.join(" ");                
        return className;
    }

    return (
        <div>
            <div className={[styles.wysiwygContent, extraClassNameWisiwygContent].join(" ")} ref={editorRef}></div>
            <div className={[styles.wisiwygBottomContainer, extraClassNameWisiwygBottomContainer].join(" ")}>
                <div className={styles.wysiwygBar}>
                    <button className={getMenuButtonClassName(isBold)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuBoldClick} title={"Bold"}><FontAwesomeIcon icon={faBold} /></button>
                    <button className={getMenuButtonClassName(isItalic)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuItalicClick} title={"Italic"}><FontAwesomeIcon icon={faItalic} /></button>
                    <button className={getMenuButtonClassName(isHighlighted)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuHighlightClick} title={"Highlight marker"}><FontAwesomeIcon icon={faHighlighter} /></button>
                    <button className={getMenuButtonClassName(false)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuOrderdedListClick} title={"Numbered list"}><FontAwesomeIcon icon={faListOl} /></button>
                    <button className={getMenuButtonClassName(false)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuUnorderedListClick} title={"Bulleted list"}><FontAwesomeIcon icon={faListUl} /></button>
                    <button className={getMenuButtonClassName(isSubscript)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuSubscriptClick} title={"Subscript"}><FontAwesomeIcon icon={faSubscript} /></button>
                    <button className={getMenuButtonClassName(isSuperscript)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuSuperscriptClick} title={"Superscript"}><FontAwesomeIcon icon={faSuperscript} /></button>
                    { isLink && <button className={getMenuButtonClassName(false)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuUnlink} title={"Unlink"}><FontAwesomeIcon icon={faUnlink} /></button> }
                    { isInList && <button className={getMenuButtonClassName(false)} onMouseDown={(event) => { event.preventDefault(); }} onClick={reduceListDepth} title={"Decrease indentation"}><FontAwesomeIcon icon={faBackward} /></button> }
                    { isInList && <button className={getMenuButtonClassName(false)} onMouseDown={(event) => { event.preventDefault(); }} onClick={increaseListDepth} title={"Increase indentation"}><FontAwesomeIcon icon={faForward} /></button> }
                    {/* <button className={getMenuButtonClassName(false)} onMouseDown={(event) => { event.preventDefault(); }} onClick={menuBoldClick}><FontAwesomeIcon icon={faQuoteRight} /><FontAwesomeIcon icon={faSortUp} /></button> */}
                </div>
            </div>
            
        </div>
    );
}